import config from "constants/config";
import React, { createContext, useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { io } from "socket.io-client";

export const SocketContext = createContext();

const SocketContextProvider = ({ children }) => {
    const business = useSelector((state) => state?.business);
    const businessId = business?._id;

    const [socketInstance, setSocketInstance] = useState(null);
    const consumerCountRef = useRef(0);

    // Manage socket instance creation & cleanup per business
    useEffect(() => {
        if (!businessId) {
            setSocketInstance(null);
            return;
        }

        const url = `${config.API_URL}/message`;
        // autoConnect is false so socket does not connect on idle app lifecycle
        const socket = io(url, {
            extraHeaders: {
                "x-business-id": businessId,
            },
            autoConnect: false,
            transports: ["websocket", "polling"],
        });

        // If consumers are already mounted and waiting, connect immediately
        if (consumerCountRef.current > 0) {
            socket.connect();
        }

        setSocketInstance(socket);

        // Disconnect old socket when business changes or user logs out
        return () => {
            socket.disconnect();
        };
    }, [businessId]);

    // Reference counting: Connect when consumers > 0, disconnect when all unmount (count === 0)
    const registerConsumer = useCallback(() => {
        consumerCountRef.current += 1;
        if (socketInstance && !socketInstance.connected) {
            socketInstance.connect();
        }

        return () => {
            consumerCountRef.current = Math.max(0, consumerCountRef.current - 1);
            if (consumerCountRef.current === 0 && socketInstance && socketInstance.connected) {
                socketInstance.disconnect();
            }
        };
    }, [socketInstance]);

    const contextValue = {
        socket: socketInstance,
        registerConsumer,
    };

    return (
        <SocketContext.Provider value={contextValue}>
            {children}
        </SocketContext.Provider>
    );
};

export default SocketContextProvider;
